import React, { useState } from 'react';
import useReports from 'state/hooks/use-reports';
import Menu from '@mui/material/Menu';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem/MenuItem';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import theme, {
  FONT_GSS_FUTURA_BOOK,
  FONT_GSS_FUTURA_SEMI_BOLD,
} from '@monorepo/shared/src/themes/defaultTheme';
import {
  remCalc,
  translate,
} from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import useFileUpload from 'state/hooks/use-file-upload';
import useSkipAssignment from 'state/hooks/use-skip-assignment';

const t = (key: string, options?) => translate('reports', key, options);

type StudentRowMoreOptionsProps = {
  student: any;
  character?: string;
  areAllAssignmentsCompleted?: boolean;
  areAllAssignmentsSkipped: boolean;
  noAssignments?: boolean;
};

const StudentRowMoreOptions: React.FC<StudentRowMoreOptionsProps> = ({
  student,
  character,
  areAllAssignmentsCompleted,
  areAllAssignmentsSkipped,
  noAssignments,
}) => {
  const classes = useStyles();
  const [, { setQuery }] = useReports();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openFileUploadModal, setOpenFileUploadModal] = useState<boolean>(false);
  const [openSkipAssignmentModal, setOpenSkipAssignmentModal] = useState<boolean>(false);
  const [isUnskip, setIsUnskip] = useState<boolean>(false);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleViewStudentReport = (event: any) => {
    event.stopPropagation();
    handleClose();
    setQuery({ selectedStudentId: student.id }, 'pushIn');
  };

  const handleFileUpload = () => {
    setOpenFileUploadModal(true);
  };

  const handleSkipOrUnskip = () => {
    setIsUnskip(areAllAssignmentsSkipped);
    setOpenSkipAssignmentModal(true);
  };

  useFileUpload({ student, character, openFileUploadModal, handleClose, setOpenFileUploadModal });
  useSkipAssignment({
    student,
    character,
    openSkipAssignmentModal,
    handleClose,
    setOpenSkipAssignmentModal,
    isUnskip,
  });

  return (
    <>
      <IconButton
        aria-label="more"
        aria-controls="long-menu"
        aria-haspopup="true"
        onClick={handleClick}
      >
        <FontAwesomeIcon icon={faEllipsisV} size="sm" color={(theme as any).palette.black.light} />
      </IconButton>
      <Menu
        id={String(student.id)}
        anchorEl={anchorEl}
        keepMounted
        open={open}
        onClose={handleClose}
      >
        <MenuItem onClick={handleViewStudentReport}>
          <Typography className={classes.menuItem}>{t('viewStudentReport')}</Typography>
        </MenuItem>
        <MenuItem onClick={handleFileUpload} disabled={noAssignments}>
          <Typography className={classes.menuItem}>{t('uploadFiles')}</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleSkipOrUnskip}
          disabled={(areAllAssignmentsCompleted || noAssignments) && !areAllAssignmentsSkipped}
        >
          <Typography className={classes.menuItem}>
            {areAllAssignmentsSkipped ? t('unskipAssignment') : t('skipAssignment')}
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

const useStyles = makeStyles(() => ({
  menuItem: {
    fontFamily: FONT_GSS_FUTURA_BOOK,
    fontWeight: 400,
    fontSize: remCalc(16),
    lineHeight: remCalc(24),
  },
  modalHeader: {
    fontSize: remCalc(24),
    lineHeight: remCalc(36),
    marginBottom: remCalc(12),
    fontFamily: FONT_GSS_FUTURA_SEMI_BOLD,
  },
  modalText: {
    fontSize: remCalc(18),
    lineHeight: remCalc(28),
    marginBottom: remCalc(12),
    fontFamily: FONT_GSS_FUTURA_BOOK,
  },
}));

export default StudentRowMoreOptions;
